// Treatment schedule maths (SPEC.md §4.3 treatment_schedule / schedule_exceptions). Pure functions;
// dates are local calendar dates as 'yyyy-MM-dd', times are 'HH:mm'.
import { addDays, format, parse } from 'date-fns';

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export type Modality = 'in_center' | 'home_hd';

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun'
};

export interface TreatmentSchedule {
  id: string;
  days_of_week: string;
  start_time: string | null;
  duration_min: number | null;
  location: string | null;
  modality: Modality | null;
  active_from: string;
  active_to: string | null;
}

export interface ScheduleException {
  date: string;
  kind: 'skip' | 'moved' | 'extra';
  new_time: string | null;
}

export interface PlannedSession {
  /** Local calendar date, 'yyyy-MM-dd'. */
  date: string;
  /** Start time 'HH:mm', or null when the schedule has no time. */
  time: string | null;
  /** Start as a Date (midnight when there is no time). */
  start: Date;
  durationMin: number | null;
  location: string | null;
  modality: Modality | null;
}

const dayKey = (date: Date): Weekday => WEEKDAYS[(date.getDay() + 6) % 7];
export const toDateKey = (date: Date) => format(date, 'yyyy-MM-dd');

export function parseDays(value: string | null | undefined): Weekday[] {
  if (!value) return [];
  const set = new Set(value.split(',').map((day) => day.trim()));
  return WEEKDAYS.filter((day) => set.has(day));
}

export function formatDays(days: Weekday[]) {
  return WEEKDAYS.filter((day) => days.includes(day)).join(',');
}

/** The schedule in force on `date`, if any (the most recently started one wins). */
export function scheduleOn(schedules: TreatmentSchedule[], date: string): TreatmentSchedule | null {
  return (
    schedules
      .filter((schedule) => schedule.active_from <= date && (schedule.active_to == null || date <= schedule.active_to))
      .sort((a, b) => (a.active_from < b.active_from ? 1 : -1))[0] ?? null
  );
}

function at(date: string, time: string | null) {
  return parse(`${date} ${time ?? '00:00'}`, 'yyyy-MM-dd HH:mm', new Date());
}

/** Planned sessions on each date in [from, to], applying one-off skips, moves and extras. */
export function plannedSessions(schedules: TreatmentSchedule[], exceptions: ScheduleException[], from: Date, to: Date): PlannedSession[] {
  const byDate = new Map<string, ScheduleException[]>();
  for (const exception of exceptions) byDate.set(exception.date, [...(byDate.get(exception.date) ?? []), exception]);

  const sessions: PlannedSession[] = [];
  for (let day = from; toDateKey(day) <= toDateKey(to); day = addDays(day, 1)) {
    const date = toDateKey(day);
    const schedule = scheduleOn(schedules, date);
    const dayExceptions = byDate.get(date) ?? [];
    const regular = !!schedule && parseDays(schedule.days_of_week).includes(dayKey(day));
    const base = {
      date,
      durationMin: schedule?.duration_min ?? null,
      location: schedule?.location ?? null,
      modality: schedule?.modality ?? null
    };

    if (regular && !dayExceptions.some((exception) => exception.kind === 'skip')) {
      const moved = dayExceptions.find((exception) => exception.kind === 'moved');
      const time = moved?.new_time ?? schedule!.start_time;
      sessions.push({ ...base, time, start: at(date, time) });
    }
    for (const extra of dayExceptions.filter((exception) => exception.kind === 'extra')) {
      sessions.push({ ...base, time: extra.new_time, start: at(date, extra.new_time) });
    }
  }
  return sessions.sort((a, b) => a.start.getTime() - b.start.getTime());
}

/**
 * The next planned session from `now`: later today if its start time hasn't passed (or today has no
 * time set), otherwise the first one after today. Looks two weeks ahead.
 */
export function nextSession(
  schedules: TreatmentSchedule[],
  exceptions: ScheduleException[],
  now: Date = new Date(),
  /** Dates that already have a session logged; those plans are done. */
  loggedDates: Set<string> = new Set()
): PlannedSession | null {
  const today = toDateKey(now);
  return (
    plannedSessions(schedules, exceptions, now, addDays(now, 14)).find((session) => {
      if (loggedDates.has(session.date)) return false;
      return session.date === today ? session.time == null || session.start.getTime() >= now.getTime() : true;
    }) ?? null
  );
}
